import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { I18nProvider } from './contexts/I18nContext';

// Pages
// Lazy load pages for better performance
import { lazy, Suspense } from 'react';

const LandingPage = lazy(() => import('./pages/landing/LandingPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));
const ClientsPage = lazy(() => import('./pages/clients/ClientsPage'));
const ServicesPage = lazy(() => import('./pages/services/ServicesPage'));
const BarbersPage = lazy(() => import('./pages/barbers/BarbersPage'));
const POSPage = lazy(() => import('./pages/pos/POSPage'));
const InventoryPage = lazy(() => import('./pages/inventory/InventoryPage'));
const PayoutsPage = lazy(() => import('./pages/payouts/PayoutsPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const PublicBookingPage = lazy(() => import('./pages/booking/PublicBookingPage'));
const BranchesPage = lazy(() => import('./pages/branches/BranchesPage'));
const PromotionsPage = lazy(() => import('./pages/promotions/PromotionsPage'));
const ExpensesPage = lazy(() => import('./pages/expenses/ExpensesPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const BookingSettingsPage = lazy(() => import('./pages/booking-settings/BookingSettingsPage'));
const SalesHistoryPage = lazy(() => import('./pages/sales/SalesHistoryPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const LeadsPage = lazy(() => import('./pages/leads/LeadsPage'));
const AdminUsersPage = lazy(() => import('./pages/leads/AdminUsersPage'));
const AdminCompaniesPage = lazy(() => import('./pages/admin/AdminCompaniesPage'));
const AdminWhatsappLogPage = lazy(() => import('./pages/admin/AdminWhatsappLogPage'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected Routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <CalendarPage />
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute>
          <ClientsPage />
        </ProtectedRoute>
      } />
      <Route path="/services" element={
        <ProtectedRoute>
          <ServicesPage />
        </ProtectedRoute>
      } />
      <Route path="/barbers" element={
        <ProtectedRoute>
          <BarbersPage />
        </ProtectedRoute>
      } />
      <Route path="/pos" element={
        <ProtectedRoute>
          <POSPage />
        </ProtectedRoute>
      } />
      <Route path="/inventory" element={
        <ProtectedRoute>
          <InventoryPage />
        </ProtectedRoute>
      } />
      <Route path="/payouts" element={
        <ProtectedRoute>
          <PayoutsPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      
      {/* Branch, Promotions and Settings routes */}
      <Route path="/branches" element={
        <ProtectedRoute>
          <BranchesPage />
        </ProtectedRoute>
      } />
      <Route path="/expenses" element={
          <ExpensesPage />
      } />
      <Route path="/promotions" element={
        <ProtectedRoute>
          <PromotionsPage />
        </ProtectedRoute>
      } />
      <Route path="/booking-settings" element={
        <ProtectedRoute>
          <BookingSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/sales-history" element={
        <ProtectedRoute>
          <SalesHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/leads" element={
        <ProtectedRoute>
          <LeadsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/companies" element={
        <ProtectedRoute>
          <AdminCompaniesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/whatsapp-log" element={
        <ProtectedRoute>
          <AdminWhatsappLogPage />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster 
              position="top-right" 
              richColors 
              closeButton
              toastOptions={{
                className: 'font-sans',
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;